/**
 * 入荷・検品フロー (2026-10-30〜) の判定と変換。
 * 管理画面 (入荷・検品ダイアログ・発注一覧・次のアクション) と functions (発送の自動作成) の両方で使う。
 *
 * 決めごと (docs/04_design/fulfillment_inspection_mockup ⑦):
 * - 付属品チェックは主軸カテゴリーが TimeWaver の商談だけ
 *     テーブルトップ型: ハードウェア本体・電源ケーブル・USBケーブル
 *     モバイル型:       ハードウェア本体・USBケーブル (電源ケーブルは付属しない)
 * - TimeWaver の機器: シリアル・保証開始日・保証終了日
 * - Dell PC: サービスタグ (シリアル番号の欄に入れる)・エクスプレスコード。保証期間は入れない
 * - 設置日は入れない (納品完了の日が自動で入る)
 * - 機器ではない商品 (ソフト等) は「シリアルなし」で入力なし
 */
import type { DeliveryLineItem, DeliveryUnitDetail } from '../types/delivery';
import type {
  FulfillmentDeviceKind,
  InspectionAccessoryKey,
  PurchaseOrder,
  PurchaseOrderInspection,
  PurchaseOrderInspectionLine,
  PurchaseOrderInspectionUnit,
  PurchaseOrderLineItem,
  PurchaseOrderStatus,
} from '../types/purchase-order';

export const INSPECTION_ACCESSORY_LABEL: Record<InspectionAccessoryKey, string> = {
  body: 'ハードウェア本体',
  power_cable: '電源ケーブル',
  usb_cable: 'USBケーブル',
};

/** 主軸カテゴリーが TimeWaver の商談で選べる種類。 */
export const TIMEWAVER_DEVICE_KINDS: FulfillmentDeviceKind[] = [
  'tw_tabletop',
  'tw_mobile',
  'dell_pc',
  'other_device',
  'none',
];
/** それ以外の商談で選べる種類。 */
export const GENERAL_DEVICE_KINDS: FulfillmentDeviceKind[] = ['other_device', 'none'];

export function deviceKindLabel(kind: FulfillmentDeviceKind, isTimeWaver: boolean): string {
  switch (kind) {
    case 'tw_tabletop':
      return 'テーブルトップ型';
    case 'tw_mobile':
      return 'モバイル型';
    case 'dell_pc':
      return 'Dell PC';
    case 'other_device':
      return isTimeWaver ? 'その他の機器（シリアルのみ）' : '機器（シリアルを入れる）';
    case 'none':
      return 'シリアルなし';
  }
}

/** 付属品チェックの項目 (TimeWaver のハードウェアだけ)。 */
export function accessoriesFor(kind: FulfillmentDeviceKind | null): InspectionAccessoryKey[] {
  if (kind === 'tw_tabletop') return ['body', 'power_cable', 'usb_cable'];
  if (kind === 'tw_mobile') return ['body', 'usb_cable'];
  return [];
}

export function hasWarranty(kind: FulfillmentDeviceKind | null): boolean {
  return kind === 'tw_tabletop' || kind === 'tw_mobile';
}

/** 台数分の入力欄を持つ種類か (none と未選択は持たない)。 */
export function needsUnits(kind: FulfillmentDeviceKind | null): boolean {
  return kind !== null && kind !== 'none';
}

/** 機器の番号欄の名前 (Dell PC はサービスタグ)。 */
export function serialFieldLabel(kind: FulfillmentDeviceKind | null | undefined): string {
  return kind === 'dell_pc' ? 'サービスタグ' : 'シリアル番号';
}

/**
 * 商品名から機器の種類を推定する。決められない商品は null (担当者が選ぶ)。
 * - 「Dell」→ Dell PC
 * - Frequency hardware / Combi / Bundle → 決めない (TimeWaver 以外は「機器」)
 * - software・Upgrade・Database・Programmes など → シリアルなし
 * - TimeWaver 以外 → 機器 (シリアルのみ)
 * - 「Mobile」→ モバイル型 / 「Pro」(TimeWaver Pro・BIZ Pro・Agrar Pro) → テーブルトップ型
 */
export function guessDeviceKind(
  item: Pick<PurchaseOrderLineItem, 'name' | 'modelName'>,
  isTimeWaver: boolean,
): FulfillmentDeviceKind | null {
  const n = `${item.name ?? ''} ${item.modelName ?? ''}`;
  if (isTimeWaver && /dell/i.test(n)) return 'dell_pc';
  if (/hardware|combi|bundle/i.test(n)) return isTimeWaver ? null : 'other_device';
  if (
    /software|pakage|package|upgrade|database|programme|license|licence|ライセンス|ソフト/i.test(n)
  ) {
    return 'none';
  }
  if (!isTimeWaver) return 'other_device';
  if (/mobile/i.test(n)) return 'tw_mobile';
  if (/\bpro\b/i.test(n)) return 'tw_tabletop';
  return null;
}

function qtyOf(item: Pick<PurchaseOrderLineItem, 'quantity'>): number {
  return Math.max(0, Math.floor(Number(item.quantity) || 0));
}

export function emptyInspectionUnit(): PurchaseOrderInspectionUnit {
  return {
    serialNumber: '',
    accessories: null,
    warrantyStartAt: null,
    warrantyEndAt: null,
    expressCode: null,
  };
}

/** 種類に合わせて台数分の欄をそろえた 1 行分 (none / 未選択は欄なし)。 */
export function normalizeInspectionLine(
  item: Pick<PurchaseOrderLineItem, 'name' | 'modelName' | 'quantity'>,
  saved: PurchaseOrderInspectionLine | null | undefined,
  isTimeWaver: boolean,
): PurchaseOrderInspectionLine {
  const deviceKind = saved ? saved.deviceKind : guessDeviceKind(item, isTimeWaver);
  if (!needsUnits(deviceKind)) return { deviceKind, units: [] };
  const units = saved?.units ?? [];
  const count = Math.max(qtyOf(item), units.length);
  return {
    deviceKind,
    units: Array.from({ length: count }, (_, i) => ({
      ...emptyInspectionUnit(),
      ...(units[i] ?? {}),
    })),
  };
}

/** 発注の明細に合わせた入荷・検品の記録 (未保存なら商品名からの推定で埋める)。 */
export function normalizeInspection(
  po: Pick<PurchaseOrder, 'lineItems' | 'inspection'>,
  isTimeWaver: boolean,
): PurchaseOrderInspection {
  const saved = po.inspection ?? null;
  return {
    arrivedAt: saved?.arrivedAt ?? null,
    inspected: !!saved?.inspected,
    inspectedAt: saved?.inspectedAt ?? null,
    inspectorUid: saved?.inspectorUid ?? null,
    inspectorName: saved?.inspectorName ?? null,
    memo: saved?.memo ?? null,
    lines: po.lineItems.map((item, i) =>
      normalizeInspectionLine(item, saved?.lines?.[i], isTimeWaver),
    ),
  };
}

function filled(v: string | null | undefined): boolean {
  return !!v && v.trim().length > 0;
}

/** 1 台分で足りない項目の名前。空配列ならそろっている。 */
export function missingUnitFields(
  kind: FulfillmentDeviceKind | null,
  unit: PurchaseOrderInspectionUnit,
): string[] {
  const out: string[] = [];
  if (!needsUnits(kind)) return out;
  for (const key of accessoriesFor(kind)) {
    if (!unit.accessories?.[key]) out.push(INSPECTION_ACCESSORY_LABEL[key]);
  }
  if (!filled(unit.serialNumber)) out.push(serialFieldLabel(kind));
  if (hasWarranty(kind)) {
    if (!filled(unit.warrantyStartAt)) out.push('保証開始日');
    if (!filled(unit.warrantyEndAt)) out.push('保証終了日');
  }
  if (kind === 'dell_pc' && !filled(unit.expressCode)) out.push('エクスプレスコード');
  return out;
}

export interface InspectionProgress {
  arrived: boolean;
  inspectedChecked: boolean;
  /** 入力が必要な台数 (none の行は含まない)。 */
  totalUnits: number;
  /** すべての項目がそろった台数。 */
  completeUnits: number;
  /** シリアル (Dell はサービスタグ) が入っている台数。 */
  filledSerials: number;
  /** 種類が未選択の行数。 */
  undecidedLines: number;
  /** 入荷・検品・全台の入力がそろい、顧客へ発送できる。 */
  ready: boolean;
}

export function inspectionProgress(
  po: Pick<PurchaseOrder, 'lineItems' | 'inspection'>,
  isTimeWaver: boolean,
): InspectionProgress {
  const ins = normalizeInspection(po, isTimeWaver);
  let totalUnits = 0;
  let completeUnits = 0;
  let filledSerials = 0;
  let undecidedLines = 0;
  for (const line of ins.lines) {
    if (line.deviceKind === null) {
      undecidedLines++;
      continue;
    }
    for (const unit of line.units) {
      totalUnits++;
      if (filled(unit.serialNumber)) filledSerials++;
      if (missingUnitFields(line.deviceKind, unit).length === 0) completeUnits++;
    }
  }
  const arrived = filled(ins.arrivedAt);
  const ready = arrived && ins.inspected && undecidedLines === 0 && completeUnits === totalUnits;
  return {
    arrived,
    inspectedChecked: ins.inspected,
    totalUnits,
    completeUnits,
    filledSerials,
    undecidedLines,
    ready,
  };
}

/**
 * 入荷・検品の記録から決まる発注の状態。
 * 発注済〜検品済の間でだけ動かす (未発注・発送済・キャンセルはそのまま)。
 */
export function statusFromInspection(
  current: PurchaseOrderStatus,
  progress: InspectionProgress,
): PurchaseOrderStatus {
  if (current !== 'ordered' && current !== 'received' && current !== 'inspected') return current;
  if (!progress.arrived) return 'ordered';
  return progress.ready ? 'inspected' : 'received';
}

/** 保証開始日から「2 年後の前日」(YYYY-MM-DD)。不正な日付は null。 */
export function defaultWarrantyEnd(startYmd: string, years = 2): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startYmd);
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]) + years, Number(m[2]) - 1, Number(m[3])));
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/**
 * 発送の明細 (顧客へ発送するときに作る)。入荷・検品で入れたシリアル・保証期間等を引き継ぐ。
 * 未入力の欄は空のまま (台数分の欄はそろえる)。
 */
export function buildDeliveryLineItems(
  po: Pick<PurchaseOrder, 'lineItems' | 'inspection'>,
  isTimeWaver: boolean,
): DeliveryLineItem[] {
  const ins = normalizeInspection(po, isTimeWaver);
  return po.lineItems.map((item, i) => {
    const line = ins.lines[i];
    const kind = line?.deviceKind ?? null;
    const base = {
      name: item.name,
      modelName: item.modelName,
      variation: item.variation ?? null,
      sku: item.sku ?? null,
      quantity: item.quantity,
    };
    if (kind === 'none') {
      return { ...base, serialNumbers: [], deviceKind: 'none', unitDetails: null };
    }
    const units = line?.units ?? [];
    const count = Math.max(qtyOf(item), units.length);
    const serialNumbers = Array.from(
      { length: count },
      (_, k) => units[k]?.serialNumber?.trim() ?? '',
    );
    const unitDetails: DeliveryUnitDetail[] = Array.from({ length: count }, (_, k) => ({
      warrantyStartAt: hasWarranty(kind) ? (units[k]?.warrantyStartAt ?? null) : null,
      warrantyEndAt: hasWarranty(kind) ? (units[k]?.warrantyEndAt ?? null) : null,
      expressCode: kind === 'dell_pc' ? units[k]?.expressCode?.trim() || null : null,
    }));
    return { ...base, serialNumbers, deviceKind: kind, unitDetails };
  });
}
